import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, PackageCheck, PackageX, Undo2, StickyNote, Sparkles, X, List, LayoutGrid, CheckCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ErrorState } from "../components/shared/ErrorState";
import { Skeleton } from "../components/ui/skeleton";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

function dotColor(status) {
  if (status === 1) return "bg-success";
  if (status === 2) return "bg-danger";
  return "bg-border";
}

export default function RoutePacking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const canListView = hasPerm("packing.list_view");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState("right");
  const [busy, setBusy] = useState(false);
  const [notesAcked, setNotesAcked] = useState({});
  const [viewMode, setViewMode] = useState("cards");
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    apiClient.get(`/routes/${id}/packing`)
      .then(({ data }) => {
        setState({ loading: false, error: null, data });
        setItems(data.items);
        const firstPending = data.items.findIndex((it) => !it.packing_status);
        setIndex(firstPending === -1 ? data.items.length : firstPending);
      })
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load this route's packing list."), data: null }));
  }, [id]);

  const total = items.length;
  const packedCount = useMemo(() => items.filter((it) => it.packing_status === 1).length, [items]);
  const skippedItems = useMemo(() => items.filter((it) => it.packing_status === 2), [items]);
  const pendingCount = total - packedCount - skippedItems.length;
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.delivery_order_id ?? 0) - (b.delivery_order_id ?? 0)),
    [items]);

  const markAllPacked = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/routes/${id}/packing/mark-all-packed`);
      setItems(data.items);
      setBulkOpen(false);
      toast.success("Marked all remaining tiffins as packed.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not mark all packed."));
    } finally {
      setBusy(false);
    }
  };

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(total, i));
    setDir(clamped >= index ? "right" : "left");
    setIndex(clamped);
  };

  const mark = async (status) => {
    const current = items[index];
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/routes/${id}/packing/${current.id}/mark`, { status });
      const nextItems = items.map((it) => (it.id === current.id ? data : it));
      setItems(nextItems);
      if (status === "pending") {
        toast.success("Reverted back to pending — mark it again when ready.");
        return;
      }
      toast.success(status === "packed" ? "Marked packed." : "Skipped — added to the skipped list.");
      const nextPending = nextItems.findIndex((it, i2) => i2 > index && !it.packing_status);
      setDir("right");
      setIndex(nextPending === -1 ? total : nextPending);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update this tiffin."));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="p-6" data-testid="route-packing-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="p-6" data-testid="route-packing-page">
        <ErrorState message={state.error || "Route not found."} onRetry={() => navigate(`/routes/${id}`)} />
      </div>
    );
  }

  const r = state.data;
  const current = items[index];
  const isSummary = index >= total;

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6" data-testid="route-packing-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/routes/${id}`)}
          className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-text-main"
          data-testid="route-packing-back-button"
        >
          <ArrowLeft size={16} strokeWidth={1.75} /> Back to Route
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-base md:text-lg" data-testid="route-packing-summary-counts">
            <span className="font-bold text-success">{packedCount} packed</span>
            {" · "}
            <span className="font-bold text-danger">{skippedItems.length} skipped</span>
            {" · "}
            <span className="font-bold text-text-main">{pendingCount} left</span>
          </p>
          {canListView && (
            <Button
              variant="outline" size="sm"
              onClick={() => setViewMode((m) => (m === "cards" ? "list" : "cards"))}
              data-testid="route-packing-view-toggle"
            >
              {viewMode === "cards"
                ? <><List size={15} strokeWidth={1.75} /> View List</>
                : <><LayoutGrid size={15} strokeWidth={1.75} /> Card View</>}
            </Button>
          )}
          {canListView && viewMode === "list" && (
            <Button
              variant="outline" size="sm"
              onClick={() => navigate(`/routes/${id}/packing/print`)}
              data-testid="route-packing-download-pdf-button"
            >
              <Download size={15} strokeWidth={1.75} /> View / Print PDF
            </Button>
          )}
          {isSuperAdmin && pendingCount > 0 && (
            <Button
              variant="accent" size="sm"
              onClick={() => setBulkOpen(true)} disabled={busy}
              data-testid="route-packing-mark-all-button"
            >
              <CheckCheck size={15} strokeWidth={1.75} /> Mark all packed
            </Button>
          )}
        </div>
      </div>

      <h1 className="mb-1 font-heading text-2xl font-semibold text-text-main">Start Packing — Route #{r.route_id}</h1>
      <p className="mb-4 text-base text-text-muted">{r.driver_name || "Unassigned"} · {total} tiffins</p>

      {viewMode === "list" && (
        <div className="space-y-3" data-testid="route-packing-list-view">
          {sortedItems.map((it, i) => (
            <div
              key={it.id}
              className="rounded-xl border border-border bg-surface p-4 md:p-5 shadow-sm"
              data-testid={`route-packing-list-row-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {it.delivery_order_id ?? i + 1}
                  </span>
                  <div>
                    <p className="text-lg font-bold text-text-main" data-testid={`route-packing-list-name-${it.id}`}>
                      {it.customer_name || `Customer for stop ${it.id}`}
                    </p>
                    <p className="mt-0.5 text-sm text-text-muted">{it.plan_name || "—"}</p>
                    {it.plan_description && (
                      <p className="mt-0.5 text-sm text-text-muted">{it.plan_description}</p>
                    )}
                  </div>
                </div>
                {it.packing_status && (
                  <span className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${it.packing_status === 1 ? "bg-success-bg text-success" : "bg-danger-bg text-danger"}`} data-testid={`route-packing-list-status-${it.id}`}>
                    {it.packing_status === 1 ? "Packed" : "Skipped"}
                  </span>
                )}
              </div>

              {it.extras?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Extras</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {it.extras.map((ex, i2) => (
                      <span key={i2} className="flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-sm font-bold text-primary" data-testid={`route-packing-list-extra-${it.id}-${i2}`}>
                        <Sparkles size={14} strokeWidth={2} /> {ex}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {it.packing_list?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Packing List</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {it.packing_list.map((pl, i2) => (
                      <span key={i2} className="flex items-baseline gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-base font-semibold text-text-main" data-testid={`route-packing-list-item-${it.id}-${i2}`}>
                        <span className="font-heading text-lg font-extrabold text-primary">{pl.quantity}×</span> {pl.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {it.packing_notes && (
                <div className="mt-3 flex items-center gap-3 rounded-lg border-2 border-warning bg-warning-bg p-3">
                  <StickyNote size={17} strokeWidth={2} className="flex-shrink-0 text-warning" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-warning">Instructions</p>
                    <p className="mt-0.5 text-lg font-bold text-danger" data-testid={`route-packing-list-notes-${it.id}`}>{it.packing_notes}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {viewMode === "cards" && (
      <>
      <div className="mb-5 h-3 w-full overflow-hidden rounded-full bg-border" data-testid="route-packing-progress-bar">
        <div className="flex h-full w-full">
          <div className="h-full bg-success transition-all" style={{ width: `${total ? (packedCount / total) * 100 : 0}%` }} />
          <div className="h-full bg-danger transition-all" style={{ width: `${total ? (skippedItems.length / total) * 100 : 0}%` }} />
        </div>
      </div>

      <div className="mb-6 flex gap-1.5 overflow-x-auto pb-1" data-testid="route-packing-dot-strip">
        {items.map((it, i) => (
          <button
            key={it.id}
            onClick={() => goTo(i)}
            title={`Tiffin ${it.delivery_order_id ?? i + 1} — ${it.customer_name || ""}`}
            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white transition-transform ${dotColor(it.packing_status)} ${i === index ? "scale-125 ring-2 ring-primary ring-offset-1" : ""}`}
            data-testid={`route-packing-dot-${it.id}`}
          >
            {it.delivery_order_id ?? i + 1}
          </button>
        ))}
      </div>

      {!isSummary && current && (
        <div key={current.id} className={`animate-slide-in-${dir} rounded-2xl border border-border bg-surface p-5 md:p-7 shadow-sm`} data-testid="route-packing-card">
          <div className="mb-5 flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-wide text-text-muted" data-testid="route-packing-card-position">
              Tiffin {current.delivery_order_id ?? index + 1} of {total}
            </span>
            {current.packing_status && (
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${current.packing_status === 1 ? "bg-success-bg text-success" : "bg-danger-bg text-danger"}`} data-testid="route-packing-card-status-badge">
                {current.packing_status === 1 ? "Packed" : "Skipped"}
              </span>
            )}
          </div>

          <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-main" data-testid="route-packing-card-customer-name">
            {current.customer_name || `Customer for stop ${current.id}`}
          </h2>
          <p className="mt-1 text-base md:text-lg text-text-muted" data-testid="route-packing-card-route-tiffin">
            Route- {r.route_id}, Tiffin- {current.delivery_order_id ?? index + 1}
          </p>

          <div className="mt-5 space-y-3.5">
            <div data-testid="route-packing-card-plan">
              <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">Plan</p>
              <p className="mt-1 text-base md:text-lg font-semibold text-text-main">{current.plan_name || "—"}</p>
              {current.plan_description && (
                <p className="mt-1 text-sm md:text-base text-text-muted" data-testid="route-packing-card-plan-description">
                  {current.plan_description}
                </p>
              )}
            </div>

            {current.extras?.length > 0 && (
              <div data-testid="route-packing-card-extras">
                <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">Extras</p>
                <div className="mt-2 flex flex-wrap gap-2.5">
                  {current.extras.map((ex, i2) => (
                    <span key={i2} className="flex items-center gap-2 rounded-full bg-accent px-3 py-1.5 text-base font-bold text-primary shadow-sm" data-testid={`route-packing-extra-${i2}`}>
                      <Sparkles size={16} strokeWidth={2} /> {ex}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {current.packing_list?.length > 0 && (
              <div data-testid="route-packing-card-packing-list">
                <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">Packing List</p>
                <div className="mt-2 flex flex-wrap gap-2.5">
                  {current.packing_list.map((pl, i2) => (
                    <span key={i2} className="flex items-baseline gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-base font-semibold text-text-main" data-testid={`route-packing-list-item-${i2}`}>
                      <span className="font-heading text-lg font-extrabold text-primary">{pl.quantity}×</span> {pl.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {current.packing_notes && (
              <div className="flex items-center gap-3 rounded-lg border-2 border-warning bg-warning-bg p-3 shadow-sm" data-testid="route-packing-card-notes">
                <div className="flex-1">
                  <p className="flex items-center gap-1.5 text-base font-bold uppercase tracking-wide text-warning">
                    <StickyNote size={19} strokeWidth={2} /> Packing Notes
                  </p>
                  <p className="mt-1 text-lg md:text-xl font-bold text-danger">{current.packing_notes}</p>
                </div>
                {!current.packing_status && (
                  <Checkbox
                    className="h-7 w-7 shrink-0 border-warning"
                    checked={!!notesAcked[current.id]}
                    onCheckedChange={(v) => setNotesAcked((m) => ({ ...m, [current.id]: Boolean(v) }))}
                    data-testid="route-packing-notes-ack"
                  />
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            {!current.packing_status ? (
              <>
                <Button className="h-14 md:h-16 w-full max-w-md text-xl font-bold" variant="accent" disabled={busy || (!!current.packing_notes && !notesAcked[current.id])} onClick={() => mark("packed")} data-testid="route-packing-mark-packed-button">
                  <PackageCheck size={24} strokeWidth={1.75} /> Mark Packed
                </Button>
                {!!current.packing_notes && !notesAcked[current.id] && (
                  <p className="text-sm font-medium text-warning" data-testid="route-packing-ack-hint">
                    Confirm you've read the packing instructions above to continue.
                  </p>
                )}
                <button
                  onClick={() => mark("skipped")} disabled={busy}
                  className="flex items-center gap-1 py-1 text-xs font-medium text-text-muted/70 hover:text-danger"
                  data-testid="route-packing-mark-skipped-button"
                >
                  <PackageX size={12} strokeWidth={1.75} /> Skip
                </button>
              </>
            ) : (
              <button
                onClick={() => mark("pending")} disabled={busy}
                className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-text-muted hover:text-text-main"
                data-testid="route-packing-undo-button"
              >
                <Undo2 size={15} strokeWidth={1.75} /> Undo — mark back as pending
              </button>
            )}
          </div>
        </div>
      )}

      {isSummary && (
        <div className="rounded-2xl border border-border bg-surface p-8 md:p-10 text-center shadow-sm" data-testid="route-packing-summary">
          <PackageCheck size={48} strokeWidth={1.5} className="mx-auto text-success" />
          <h2 className="mt-3 font-heading text-2xl font-semibold text-text-main">
            {pendingCount === 0 ? "All tiffins reviewed!" : "Nice progress"}
          </h2>
          <p className="mt-1 text-lg text-text-muted">
            {packedCount} packed, {skippedItems.length} skipped out of {total} on this route.
          </p>
          {skippedItems.length > 0 && (
            <div className="mt-4 rounded-lg border border-danger/30 bg-danger-bg p-4 text-left" data-testid="route-packing-summary-skipped-list">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-danger">Tell the driver — skipped from packing</p>
              <ul className="space-y-1">
                {skippedItems.map((it) => (
                  <li key={it.id} className="text-base text-text-main">
                    Tiffin {it.delivery_order_id} — {it.customer_name || `Delivery #${it.id}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="outline" onClick={() => goTo(0)} data-testid="route-packing-review-again-button">Review Again</Button>
            <Button variant="accent" onClick={() => navigate(`/routes/${id}`)} data-testid="route-packing-close-button">Back to Route</Button>
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        <Button variant="secondary" size="sm" disabled={index === 0} onClick={() => goTo(index - 1)} data-testid="route-packing-prev-button">
          <ChevronLeft size={15} strokeWidth={2} /> Prev
        </Button>
        <span className="text-sm text-text-muted">{Math.min(index + 1, total)} / {total}{isSummary ? " · summary" : ""}</span>
        <Button variant="secondary" size="sm" disabled={index >= total} onClick={() => goTo(index + 1)} data-testid="route-packing-next-button">
          Next <ChevronRight size={15} strokeWidth={2} />
        </Button>
      </div>

      {skippedItems.length > 0 && !isSummary && (
        <div className="mt-5 rounded-lg border border-danger/30 bg-danger-bg p-4" data-testid="route-packing-skipped-strip">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-danger">
            <PackageX size={14} strokeWidth={1.75} /> Skipped so far ({skippedItems.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {skippedItems.map((it) => {
              const i2 = items.findIndex((x) => x.id === it.id);
              return (
                <button
                  key={it.id} onClick={() => goTo(i2)}
                  className="flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-sm font-medium text-danger shadow-sm hover:bg-danger-bg"
                  data-testid={`route-packing-skipped-chip-${it.id}`}
                >
                  #{it.delivery_order_id} {it.customer_name || ""} <X size={12} strokeWidth={2} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}

      <ConfirmDialog
        open={bulkOpen} onOpenChange={setBulkOpen}
        title={`Mark all ${pendingCount} remaining tiffin${pendingCount === 1 ? "" : "s"} as packed?`}
        description="This marks every tiffin that hasn't been decided yet as packed. Already-skipped tiffins stay skipped. Use only when you're sure the whole route is packed."
        confirmLabel="Mark all packed" variant="accent" loading={busy} onConfirm={markAllPacked}
        testId="route-packing-mark-all-confirm-dialog"
      />
    </div>
  );
}
